/**
 * §3.1 binding table + §3.9 remapping.
 *
 * Two slots per action (`primary`, `alt`) **per device class**. §3.9 pins that device classes
 * (keyboard, mouse, gamepad) conflict only within their class; the §3.1 table itself lists a
 * KB+Mouse column with an alt and a Gamepad column with an alt, so one slot pair per class is the
 * shape that reproduces the table bitwise.
 *
 * Conflict scope is (device class × context group). The §3.1 table binds Esc to both Pause and
 * UI back, and the gamepad A button to both UI confirm and run-summary restart; those are distinct
 * screens, not a double-bind, so each action carries a context group and a steal only ever fires
 * inside one group. Reserved codes (Esc) can never be stolen or cleared from Pause.
 */

import { cfg } from '../core/config';
import type { MovementScheme } from './follow';

/** Flat persisted binding shape: `{ <action>: { primary, alt } }`. Owned by the UI's SettingsStore. */
export type Bindings = Record<string, { primary: string; alt: string }>;

export type DeviceClass = 'keyboard' | 'mouse' | 'gamepad';
export type BindingSlot = 'primary' | 'alt';

/** §3.9 — an empty slot. Persisted verbatim. */
export const UNBOUND = 'UNBOUND';

export const ACTIONS = [
  'move_up',
  'move_down',
  'move_left',
  'move_right',
  'focus_aim',
  'dash',
  'pause',
  'select_1',
  'select_2',
  'select_3',
  'reroll',
  'ui_confirm',
  'ui_back',
  'restart',
] as const;

export type ActionId = (typeof ACTIONS)[number];

/** Steal scope. Two actions in different groups may share an input without conflicting. */
export type ContextGroup = 'game' | 'system' | 'levelup' | 'ui' | 'summary';

export const ACTION_GROUP: Readonly<Record<ActionId, ContextGroup>> = {
  move_up: 'game',
  move_down: 'game',
  move_left: 'game',
  move_right: 'game',
  focus_aim: 'game',
  dash: 'game',
  pause: 'system',
  select_1: 'levelup',
  select_2: 'levelup',
  select_3: 'levelup',
  reroll: 'levelup',
  ui_confirm: 'ui',
  ui_back: 'ui',
  restart: 'summary',
};

export const ACTION_INDEX: Readonly<Record<ActionId, number>> = (() => {
  const out: Record<string, number> = {};
  for (let i = 0; i < ACTIONS.length; i++) out[ACTIONS[i]] = i;
  return out as Record<ActionId, number>;
})();

export function actionBit(a: ActionId): number {
  return 1 << ACTION_INDEX[a];
}

export interface Slots {
  primary: string;
  alt: string;
}

export type ActionBinding = Record<DeviceClass, Slots>;
export type BindingMap = Record<ActionId, ActionBinding>;

/**
 * Binding code grammar (stable, persisted):
 *   keyboard  `k:<KeyboardEvent.code>`            e.g. `k:KeyW`, `k:Space`, `k:Escape`
 *   mouse     `m:<button index>`                  e.g. `m:0` LMB, `m:2` RMB
 *   gamepad   `g:b<button index>`                 standard mapping button
 *             `g:ax<axis index><+|->`             axis half-range
 *             `g:lstick` / `g:rstick`             whole analog stick
 */
export function codeClass(code: string): DeviceClass | null {
  if (code === UNBOUND) return null;
  if (code.charCodeAt(0) === 107 /* k */ && code.charCodeAt(1) === 58) return 'keyboard';
  if (code.charCodeAt(0) === 109 /* m */ && code.charCodeAt(1) === 58) return 'mouse';
  if (code.charCodeAt(0) === 103 /* g */ && code.charCodeAt(1) === 58) return 'gamepad';
  return null;
}

/** §3.9 — Esc always pauses regardless of bindings. Never stolen, never cleared. */
export const RESERVED_CODES: readonly string[] = ['k:Escape'];
export const RESERVED_OWNER: ActionId = 'pause';

export function isReservedCode(code: string): boolean {
  for (let i = 0; i < RESERVED_CODES.length; i++) if (RESERVED_CODES[i] === code) return true;
  return false;
}

function slots(primary: string, alt: string): Slots {
  return { primary, alt };
}

function empty(): Slots {
  return { primary: UNBOUND, alt: UNBOUND };
}

/** The §3.1 table, verbatim. `restoreDefaults()` reproduces exactly this. */
export function defaultBindings(): BindingMap {
  return {
    move_up: { keyboard: slots('k:KeyW', 'k:ArrowUp'), mouse: empty(), gamepad: slots('g:ax1-', 'g:b12') },
    move_down: { keyboard: slots('k:KeyS', 'k:ArrowDown'), mouse: empty(), gamepad: slots('g:ax1+', 'g:b13') },
    move_left: { keyboard: slots('k:KeyA', 'k:ArrowLeft'), mouse: empty(), gamepad: slots('g:ax0-', 'g:b14') },
    move_right: { keyboard: slots('k:KeyD', 'k:ArrowRight'), mouse: empty(), gamepad: slots('g:ax0+', 'g:b15') },
    focus_aim: { keyboard: empty(), mouse: slots('m:2', UNBOUND), gamepad: slots('g:rstick', UNBOUND) },
    dash: { keyboard: slots('k:Space', 'k:ShiftLeft'), mouse: empty(), gamepad: slots('g:b5', 'g:b0') },
    pause: { keyboard: slots('k:Escape', UNBOUND), mouse: empty(), gamepad: slots('g:b9', UNBOUND) },
    select_1: { keyboard: slots('k:Digit1', UNBOUND), mouse: empty(), gamepad: empty() },
    select_2: { keyboard: slots('k:Digit2', UNBOUND), mouse: empty(), gamepad: empty() },
    select_3: { keyboard: slots('k:Digit3', UNBOUND), mouse: empty(), gamepad: empty() },
    reroll: { keyboard: slots('k:KeyR', UNBOUND), mouse: empty(), gamepad: slots('g:b2', UNBOUND) },
    ui_confirm: { keyboard: empty(), mouse: slots('m:0', UNBOUND), gamepad: slots('g:b0', UNBOUND) },
    ui_back: { keyboard: slots('k:Escape', UNBOUND), mouse: empty(), gamepad: slots('g:b1', UNBOUND) },
    restart: { keyboard: slots('k:Enter', UNBOUND), mouse: empty(), gamepad: slots('g:b0', UNBOUND) },
  };
}

const DEVICE_CLASSES: readonly DeviceClass[] = ['keyboard', 'mouse', 'gamepad'];
const SLOT_NAMES: readonly BindingSlot[] = ['primary', 'alt'];

/** Result of a steal, for the §13 remap UI. */
export interface StealRecord {
  action: ActionId;
  device: DeviceClass;
  slot: BindingSlot;
}

export class BindingTable {
  map: BindingMap = defaultBindings();
  /** `${action}|${device}|${slot}` for every slot emptied by a steal (§3.9 "flagged in the UI"). */
  private flags = new Set<string>();
  /** code → bitmask of action indices, per device class. Rebuilt on every mutation. */
  private lookup: Record<DeviceClass, Map<string, number>> = {
    keyboard: new Map(),
    mouse: new Map(),
    gamepad: new Map(),
  };
  /** Bumped on every mutation so consumers can cheaply detect a rebind. */
  revision = 0;

  constructor() {
    this.rebuild();
  }

  private rebuild(): void {
    for (let d = 0; d < DEVICE_CLASSES.length; d++) this.lookup[DEVICE_CLASSES[d]].clear();
    for (let i = 0; i < ACTIONS.length; i++) {
      const action = ACTIONS[i];
      const bit = 1 << i;
      for (let d = 0; d < DEVICE_CLASSES.length; d++) {
        const dev = DEVICE_CLASSES[d];
        const s = this.map[action][dev];
        const table = this.lookup[dev];
        if (s.primary !== UNBOUND) table.set(s.primary, (table.get(s.primary) ?? 0) | bit);
        if (s.alt !== UNBOUND) table.set(s.alt, (table.get(s.alt) ?? 0) | bit);
      }
    }
    this.revision++;
  }

  /** Bitmask of actions bound to `code` in `device`. Allocation-free at event time. */
  actionsFor(device: DeviceClass, code: string): number {
    return this.lookup[device].get(code) ?? 0;
  }

  get(action: ActionId, device: DeviceClass, slot: BindingSlot): string {
    return this.map[action][device][slot];
  }

  /**
   * §3.9 — assigning an in-use input steals it. The previous owner's slot becomes UNBOUND and is
   * flagged. Conflicts never resolve by refusal. Returns every slot that was emptied.
   */
  assign(action: ActionId, device: DeviceClass, slot: BindingSlot, code: string, out?: StealRecord[]): StealRecord[] {
    const result = out ?? [];
    result.length = 0;
    if (code !== UNBOUND && codeClass(code) !== device) return result;
    if (isReservedCode(code) && action !== RESERVED_OWNER && ACTION_GROUP[action] === ACTION_GROUP[RESERVED_OWNER]) {
      return result;
    }
    if (code !== UNBOUND) {
      const group = ACTION_GROUP[action];
      for (let i = 0; i < ACTIONS.length; i++) {
        const other = ACTIONS[i];
        if (ACTION_GROUP[other] !== group) continue;
        for (let s = 0; s < SLOT_NAMES.length; s++) {
          const os = SLOT_NAMES[s];
          if (other === action && os === slot) continue;
          if (this.map[other][device][os] !== code) continue;
          if (other === RESERVED_OWNER && isReservedCode(code)) continue;
          this.map[other][device][os] = UNBOUND;
          this.flags.add(flagKey(other, device, os));
          result.push({ action: other, device, slot: os });
        }
      }
    }
    if (action === RESERVED_OWNER && isReservedCode(this.map[action][device][slot]) && code === UNBOUND) {
      return result;
    }
    this.map[action][device][slot] = code;
    this.flags.delete(flagKey(action, device, slot));
    this.rebuild();
    return result;
  }

  clear(action: ActionId, device: DeviceClass, slot: BindingSlot): boolean {
    if (action === RESERVED_OWNER && isReservedCode(this.map[action][device][slot])) return false;
    this.map[action][device][slot] = UNBOUND;
    this.flags.delete(flagKey(action, device, slot));
    this.rebuild();
    return true;
  }

  isFlagged(action: ActionId, device: DeviceClass, slot: BindingSlot): boolean {
    return this.flags.has(flagKey(action, device, slot));
  }

  clearFlags(): void {
    this.flags.clear();
  }

  /** §3.9 per-action reset. */
  resetAction(action: ActionId): void {
    const def = defaultBindings()[action];
    for (let d = 0; d < DEVICE_CLASSES.length; d++) {
      const dev = DEVICE_CLASSES[d];
      this.map[action][dev].primary = def[dev].primary;
      this.map[action][dev].alt = def[dev].alt;
      this.flags.delete(flagKey(action, dev, 'primary'));
      this.flags.delete(flagKey(action, dev, 'alt'));
    }
    this.rebuild();
  }

  /** §3.9 global restore-defaults — reproduces the §3.1 table bitwise. */
  restoreDefaults(): void {
    this.map = defaultBindings();
    this.flags.clear();
    this.rebuild();
  }

  /** Every (class, group) pair holds at most one owner per code. Used by the §3.9 acceptance test. */
  validate(): string[] {
    const problems: string[] = [];
    for (let d = 0; d < DEVICE_CLASSES.length; d++) {
      const dev = DEVICE_CLASSES[d];
      const owners = new Map<string, number>();
      for (let i = 0; i < ACTIONS.length; i++) {
        const action = ACTIONS[i];
        const group = ACTION_GROUP[action];
        for (let s = 0; s < SLOT_NAMES.length; s++) {
          const code = this.map[action][dev][SLOT_NAMES[s]];
          if (code === UNBOUND) continue;
          const key = `${group}|${code}`;
          const n = (owners.get(key) ?? 0) + 1;
          owners.set(key, n);
          if (n > 1) problems.push(`${dev} ${group} ${code} has ${n} owners`);
        }
      }
    }
    return problems;
  }

  serialize(): Record<string, ActionBinding> {
    const out: Record<string, ActionBinding> = {};
    for (let i = 0; i < ACTIONS.length; i++) {
      const a = ACTIONS[i];
      out[a] = {
        keyboard: { primary: this.map[a].keyboard.primary, alt: this.map[a].keyboard.alt },
        mouse: { primary: this.map[a].mouse.primary, alt: this.map[a].mouse.alt },
        gamepad: { primary: this.map[a].gamepad.primary, alt: this.map[a].gamepad.alt },
      };
    }
    return out;
  }

  /** Tolerant of partial / unknown data: anything missing keeps its default. */
  deserialize(data: unknown): void {
    this.map = defaultBindings();
    this.flags.clear();
    if (data && typeof data === 'object') {
      const rec = data as Record<string, unknown>;
      for (let i = 0; i < ACTIONS.length; i++) {
        const a = ACTIONS[i];
        const entry = rec[a];
        if (!entry || typeof entry !== 'object') continue;
        const per = entry as Record<string, unknown>;
        for (let d = 0; d < DEVICE_CLASSES.length; d++) {
          const dev = DEVICE_CLASSES[d];
          const sl = per[dev];
          if (!sl || typeof sl !== 'object') continue;
          const pair = sl as Record<string, unknown>;
          for (let s = 0; s < SLOT_NAMES.length; s++) {
            const name = SLOT_NAMES[s];
            const v = pair[name];
            if (typeof v !== 'string') continue;
            if (v !== UNBOUND && codeClass(v) !== dev) continue;
            this.map[a][dev][name] = v;
          }
        }
      }
    }
    this.rebuild();
  }
}

function flagKey(action: ActionId, device: DeviceClass, slot: BindingSlot): string {
  return `${action}|${device}|${slot}`;
}

// --- human-readable labels for the §13 remap screen ---

const KEY_LABELS: Readonly<Record<string, string>> = {
  'k:Space': 'SPACE',
  'k:ShiftLeft': 'L SHIFT',
  'k:ShiftRight': 'R SHIFT',
  'k:Escape': 'ESC',
  'k:Enter': 'ENTER',
  'k:ArrowUp': 'UP',
  'k:ArrowDown': 'DOWN',
  'k:ArrowLeft': 'LEFT',
  'k:ArrowRight': 'RIGHT',
  'm:0': 'LMB',
  'm:1': 'MMB',
  'm:2': 'RMB',
  'g:lstick': 'L STICK',
  'g:rstick': 'R STICK',
  'g:b0': 'A',
  'g:b1': 'B',
  'g:b2': 'X',
  'g:b3': 'Y',
  'g:b4': 'LB',
  'g:b5': 'RB',
  'g:b6': 'LT',
  'g:b7': 'RT',
  'g:b8': 'SELECT',
  'g:b9': 'START',
  'g:b10': 'L3',
  'g:b11': 'R3',
  'g:b12': 'DPAD UP',
  'g:b13': 'DPAD DOWN',
  'g:b14': 'DPAD LEFT',
  'g:b15': 'DPAD RIGHT',
  'g:ax0-': 'L STICK LEFT',
  'g:ax0+': 'L STICK RIGHT',
  'g:ax1-': 'L STICK UP',
  'g:ax1+': 'L STICK DOWN',
};

export function describeCode(code: string): string {
  if (code === UNBOUND) return 'UNBOUND';
  const label = KEY_LABELS[code];
  if (label) return label;
  if (code.startsWith('k:Key')) return code.slice(5).toUpperCase();
  if (code.startsWith('k:Digit')) return code.slice(7);
  if (code.startsWith('k:')) return code.slice(2).toUpperCase();
  return code;
}


// --- A.6 settings.v1 persistence (input subtree only) ---
//
// The persisted `Bindings` shape is flat (`{ <action>: { primary, alt } }`), one slot pair per
// action. This table carries a slot pair per device class, so each stored slot is the three class
// codes joined with `|` in the fixed order keyboard|mouse|gamepad. A single-token value (e.g.
// `KeyW`, `Mouse2`) is routed to the class its shape implies, so a settings blob written by any
// other owner still loads.

export interface DeadzoneSettings {
  inner: number;
  outer: number;
}

/** `save.ts` names UI confirm/back without the `ui_` prefix; keep its keys stable. */
const SAVE_KEY_FOR: Readonly<Record<ActionId, string>> = {
  move_up: 'move_up',
  move_down: 'move_down',
  move_left: 'move_left',
  move_right: 'move_right',
  focus_aim: 'focus_aim',
  dash: 'dash',
  pause: 'pause',
  select_1: 'select_1',
  select_2: 'select_2',
  select_3: 'select_3',
  reroll: 'reroll',
  ui_confirm: 'confirm',
  ui_back: 'back',
  restart: 'restart',
};

const CLASS_ORDER: readonly DeviceClass[] = ['keyboard', 'mouse', 'gamepad'];

function encodeSlot(binding: ActionBinding, slot: BindingSlot): string {
  return binding.keyboard[slot] + '|' + binding.mouse[slot] + '|' + binding.gamepad[slot];
}

/** Accepts the joined form and the single-token legacy form. Writes into `outSlots`. */
function decodeSlot(value: string, outSlots: Record<DeviceClass, string>): void {
  outSlots.keyboard = UNBOUND;
  outSlots.mouse = UNBOUND;
  outSlots.gamepad = UNBOUND;
  if (typeof value !== 'string' || value === '') return;
  if (value.indexOf('|') >= 0) {
    const parts = value.split('|');
    for (let i = 0; i < CLASS_ORDER.length && i < parts.length; i++) {
      const code = parts[i];
      if (code === UNBOUND || code === '') continue;
      if (codeClass(code) === CLASS_ORDER[i]) outSlots[CLASS_ORDER[i]] = code;
    }
    return;
  }
  if (value === UNBOUND) return;
  const cls = codeClass(value);
  if (cls) {
    outSlots[cls] = value;
    return;
  }
  // legacy bare code from save.ts's own default table
  const mouse = /^Mouse(\d+)$/.exec(value);
  if (mouse) outSlots.mouse = 'm:' + mouse[1];
  else outSlots.keyboard = 'k:' + value;
}

export function serializeFlat(table: BindingTable): Bindings {
  const out: Bindings = {};
  for (let i = 0; i < ACTIONS.length; i++) {
    const a = ACTIONS[i];
    const b = table.map[a];
    out[SAVE_KEY_FOR[a]] = { primary: encodeSlot(b, 'primary'), alt: encodeSlot(b, 'alt') };
  }
  return out;
}

export function applyFlat(table: BindingTable, flat: unknown): void {
  const nested: Record<string, ActionBinding> = {};
  const tmp: Record<DeviceClass, string> = { keyboard: UNBOUND, mouse: UNBOUND, gamepad: UNBOUND };
  if (flat && typeof flat === 'object') {
    const rec = flat as Record<string, unknown>;
    for (let i = 0; i < ACTIONS.length; i++) {
      const a = ACTIONS[i];
      const entry = rec[SAVE_KEY_FOR[a]];
      if (!entry || typeof entry !== 'object') continue;
      const pair = entry as Record<string, unknown>;
      const binding: ActionBinding = {
        keyboard: { primary: UNBOUND, alt: UNBOUND },
        mouse: { primary: UNBOUND, alt: UNBOUND },
        gamepad: { primary: UNBOUND, alt: UNBOUND },
      };
      for (let s = 0; s < 2; s++) {
        const name: BindingSlot = s === 0 ? 'primary' : 'alt';
        const v = pair[name];
        if (typeof v !== 'string') continue;
        decodeSlot(v, tmp);
        binding.keyboard[name] = tmp.keyboard;
        binding.mouse[name] = tmp.mouse;
        binding.gamepad[name] = tmp.gamepad;
      }
      nested[a] = binding;
    }
  }
  table.deserialize(nested);
}

/** Touch tuning persisted alongside the bindings (AMD-1). */
export interface TouchSettings {
  stick_radius: number;
  tap_ms: number;
  tap_slop: number;
  /** Gap from a tap's release to the next touch-down that still fires dash. See `touch.ts`. */
  double_tap_ms: number;
  /** Inert since touch roles went by arrival order; kept so stored blobs keep their shape. */
  left_handed: boolean;
  /** AMD-1 twin-stick, or whole-surface pointer-follow. Default `stick`. */
  scheme: MovementScheme;
}

/**
 * Where input settings live. The UI's `SettingsStore` (§13.1.3) is the single owner of
 * `lumenabyss.settings.v1`; this module never touches localStorage itself. Two writers on
 * one key with different envelopes deleted each other's payload on every boot, so the
 * integrator injects the store here and the UI stays authoritative.
 */
export interface InputSettingsPort {
  readBindings(): Record<string, { primary: string; alt: string }> | null;
  writeBindings(flat: Record<string, { primary: string; alt: string }>): void;
  readDeadzone(): { inner: number; outer: number } | null;
  writeDeadzone(dz: { inner: number; outer: number }): void;
  readTouch(): TouchSettings | null;
  writeTouch(t: TouchSettings): void;
}

let port: InputSettingsPort | null = null;

/** Called once by the integrator with the UI's settings store. Unset = in-memory only. */
export function setInputSettingsPort(p: InputSettingsPort | null): void {
  port = p;
}

export function saveInputSettings(table: BindingTable, deadzone: DeadzoneSettings, touch?: TouchSettings): void {
  if (!port) return;
  port.writeBindings(serializeFlat(table));
  port.writeDeadzone({ inner: deadzone.inner, outer: deadzone.outer });
  if (touch) port.writeTouch(touch);
}

/** Fills `out` and `outTouch` from the injected store, falling back to §3.5 / AMD-1 defaults. */
export function loadInputSettings(
  table: BindingTable,
  out: DeadzoneSettings,
  outTouch?: TouchSettings,
): DeadzoneSettings {
  const flat = port?.readBindings() ?? null;
  if (flat) applyFlat(table, flat);
  const dz = port?.readDeadzone() ?? null;
  out.inner = dz && dz.inner >= 0 && dz.inner < 1 ? dz.inner : 0.18;
  out.outer = dz && dz.outer > out.inner && dz.outer <= 1 ? dz.outer : 0.92;
  if (outTouch) {
    const t = port?.readTouch() ?? null;
    outTouch.stick_radius = t && t.stick_radius >= 16 ? t.stick_radius : 44;
    outTouch.tap_ms = t && t.tap_ms > 0 ? t.tap_ms : 180;
    outTouch.tap_slop = t && t.tap_slop >= 0 ? t.tap_slop : 12;
    // the A.5 registry owns the default, so a designer moving `input.double_tap_ms` actually moves
    // the gesture for every player who has not overridden it
    outTouch.double_tap_ms = t && t.double_tap_ms > 0 ? t.double_tap_ms : cfg.get('input.double_tap_ms');
    outTouch.left_handed = t?.left_handed === true;
    outTouch.scheme = t?.scheme === 'follow' ? 'follow' : 'stick';
  }
  return out;
}
